import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/pages/IndustriesPage.css';

const IndustriesPage = () => {
  const industries = [
    {
      name: 'Healthcare & Medical',
      icon: '🏥',
      description: 'Digital solutions for hospitals, clinics, and healthcare providers',
      services: ['Patient Management Systems', 'Telemedicine Platforms', 'Health Analytics', 'Medical Records Management']
    },
    {
      name: 'Financial Services',
      icon: '💰',
      description: 'Secure fintech solutions for banks, insurance, and investment firms',
      services: ['Banking Apps', 'Payment Gateways', 'Trading Platforms', 'Financial Analytics']
    },
    {
      name: 'Education & E-Learning',
      icon: '🎓',
      description: 'Modern learning platforms for schools, universities, and training centers',
      services: ['LMS Platforms', 'Virtual Classrooms', 'Student Portals', 'Course Management']
    },
    {
      name: 'E-Commerce & Retail',
      icon: '🛒',
      description: 'Scalable online stores and retail management solutions',
      services: ['Online Stores', 'Inventory Systems', 'Point of Sale', 'Multi-vendor Marketplaces']
    },
    {
      name: 'Real Estate & Property',
      icon: '🏢',
      description: 'Property management and real estate marketplace platforms',
      services: ['Property Listings', 'Virtual Tours', 'Tenant Management', 'CRM Systems']
    },
    {
      name: 'Manufacturing & Industry',
      icon: '🏭',
      description: 'Industrial automation and supply chain management',
      services: ['Production Tracking', 'Supply Chain', 'Quality Control', 'IoT Integration']
    },
    {
      name: 'Hospitality & Tourism',
      icon: '🏨',
      description: 'Booking systems and guest management for hotels and travel',
      services: ['Booking Platforms', 'Guest Management', 'Tour Planning', 'Review Systems']
    },
    {
      name: 'Agriculture & Agritech',
      icon: '🌾',
      description: 'Smart farming solutions and agricultural marketplaces',
      services: ['Farm Management', 'Crop Monitoring', 'Agri Marketplaces', 'Supply Chain']
    },
    {
      name: 'Technology & Startups',
      icon: '🚀',
      description: 'MVP development and scaling solutions for tech companies',
      services: ['MVP Development', 'SaaS Platforms', 'API Development', 'Cloud Infrastructure']
    },
    {
      name: 'Media & Entertainment',
      icon: '🎬',
      description: 'Content platforms and streaming solutions',
      services: ['Streaming Platforms', 'Content Management', 'Social Networks', 'Digital Publishing']
    },
    {
      name: 'Logistics & Transportation',
      icon: '🚚',
      description: 'Fleet management and delivery tracking systems',
      services: ['Fleet Tracking', 'Route Optimization', 'Warehouse Management', 'Delivery Apps']
    },
    {
      name: 'Government & Public Sector',
      icon: '🏛️',
      description: 'Digital transformation for government agencies',
      services: ['Citizen Portals', 'Document Management', 'E-Government Services', 'Public Data Systems']
    },
    {
      name: 'Non-Profit & NGOs',
      icon: '🤝',
      description: 'Digital tools for social impact organizations',
      services: ['Donor Management', 'Campaign Platforms', 'Volunteer Coordination', 'Impact Tracking']
    },
    {
      name: 'Food & Beverage',
      icon: '🍽️',
      description: 'Restaurant management and food delivery platforms',
      services: ['Online Ordering', 'Table Reservations', 'Kitchen Management', 'Delivery Systems']
    },
    {
      name: 'Telecommunications',
      icon: '📡',
      description: 'Network management and customer service platforms',
      services: ['Customer Portals', 'Billing Systems', 'Network Monitoring', 'Service Management']
    },
    {
      name: 'Energy & Utilities',
      icon: '⚡',
      description: 'Smart grid solutions and utility management',
      services: ['Smart Metering', 'Energy Management', 'Billing Systems', 'Grid Monitoring']
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 15 },
    visible: {
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="industries-page">
      <section className="industries-hero">
        <div className="industries-hero-content">
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-badge"
          >
            Industries We Serve
          </motion.div>

          <motion.h1
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="industries-hero-title"
          >
            Empowering <span className="gradient-text">Diverse Sectors</span> with Digital Innovation
          </motion.h1>

          <motion.p
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="industries-hero-subtitle"
          >
            From healthcare to finance, education to e-commerce - we deliver tailored digital solutions
            that drive growth and efficiency across diverse industries worldwide.
          </motion.p>
        </div>
      </section>

      <section className="industries-list">
        <div className="industries-container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="industries-grid"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="industry-card"
              >
                <div className="industry-icon">{industry.icon}</div>
                <h3 className="industry-name">{industry.name}</h3>
                <p className="industry-description">{industry.description}</p>

                <div className="industry-services">
                  <h4>Key Solutions:</h4>
                  <ul>
                    {industry.services.map((service, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.707 4.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L7 9.586l5.293-5.293a1 1 0 011.414 0z" fill="currentColor"/>
                        </svg>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to="/contact" className="industry-cta">
                  Discuss Your Project
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="industries-cta-section">
        <div className="industries-cta-container">
          <div className="industries-cta-content">
            <h2>Don't See Your Industry?</h2>
            <p>
              We work with businesses across all sectors. If your industry isn't listed,
              we'd love to discuss how we can create a custom solution for you.
            </p>
            <div className="industries-cta-buttons">
              <Link to="/contact" className="primary-button">
                Contact Us
              </Link>
              <Link to="/services" className="secondary-button">
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
